import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "pixso-tabbed-video__surface" }
const _hoisted_2 = ["poster", "aria-label", "onLoadeddata", "onCanplay"]
const _hoisted_3 = ["src"]
const _hoisted_4 = { key: 0 }
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["aria-selected", "onClick"]
const _hoisted_7 = {
  key: 0,
  class: "pixso-tabbed-video__subtitle pixso-body-md"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export interface TabbedVideoPlayerItem {
  key: string
  src: string
  buttonLabel: string
  subtitle?: string
  ariaLabel?: string
}


const __default__ = {
  inheritAttrs: false
}

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'TabbedVideoPlayer',
  props: {
    items: {},
    poster: { default: '' },
    caption: { default: '' },
    tablistLabel: { default: '' },
    initialIndex: { default: 0 }
  },
  emits: ["change"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const DESKTOP_ROW_COUNT = 5
const COMPACT_ROW_COUNT = 4
const MOBILE_ROW_COUNT = 3
const MOBILE_ROW_COUNT_EN = 2
const MIN_ITEMS_PER_ROW = 2

const { locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')
const selectedIndex = ref(0)
const primaryIndex = ref<number | null>(0)
const secondaryIndex = ref<number | null>(null)
const activeLayer = ref<'primary' | 'secondary'>('primary')
const loadingLayer = ref<'primary' | 'secondary' | null>(null)
const loadingItemKey = ref('')
const viewportMode = ref<'desktop' | 'compact' | 'mobile'>('desktop')

let compactQuery: MediaQueryList | null = null
let mobileQuery: MediaQueryList | null = null

const clampIndex = (value: number) => {
  if (!props.items.length) {
    return 0
  }

  const index = Number.isFinite(value) ? Math.trunc(value) : 0
  return Math.min(Math.max(index, 0), props.items.length - 1)
}

const indexedItems = computed(() => (
  props.items.map((item, itemIndex) => ({
    ...item,
    itemIndex
  }))
))

const rebalanceOrphanRows = <T,>(rows: T[][]) => {
  if (MIN_ITEMS_PER_ROW <= 1) {
    return rows
  }

  const balancedRows = rows.map((row) => [...row])

  for (let index = balancedRows.length - 1; index > 0; index -= 1) {
    if (balancedRows[index].length !== 1) {
      continue
    }

    const previousRow = balancedRows[index - 1]

    if (previousRow.length > MIN_ITEMS_PER_ROW) {
      balancedRows[index].unshift(previousRow.pop() as T)
    } else {
      previousRow.push(...balancedRows[index])
      balancedRows.splice(index, 1)
    }
  }

  return balancedRows
}

const chunkItems = <T,>(items: T[], preferredRowCount: number) => {
  const rowCount = Math.max(Math.trunc(preferredRowCount), MIN_ITEMS_PER_ROW)

  if (items.length <= rowCount) {
    return [items]
  }

  const rows: T[][] = []
  for (let index = 0; index < items.length; index += rowCount) {
    rows.push(items.slice(index, index + rowCount))
  }

  return rebalanceOrphanRows(rows)
}

const preferredRowCount = computed(() => {
  if (viewportMode.value === 'mobile') {
    return isEnglish.value ? MOBILE_ROW_COUNT_EN : MOBILE_ROW_COUNT
  }

  if (viewportMode.value === 'compact') {
    return COMPACT_ROW_COUNT
  }

  return DESKTOP_ROW_COUNT
})

const itemRows = computed(() => {
  return chunkItems(indexedItems.value, preferredRowCount.value).filter((row) => row.length)
})

const getItemKey = (item: TabbedVideoPlayerItem) => `${item.key}-${item.src}`

const getLayerIndex = (layer: 'primary' | 'secondary') => {
  return layer === 'primary' ? primaryIndex.value : secondaryIndex.value
}

const setLayerIndex = (layer: 'primary' | 'secondary', index: number | null) => {
  if (layer === 'primary') {
    primaryIndex.value = index
    return
  }

  secondaryIndex.value = index
}

const inactiveLayer = computed<'primary' | 'secondary'>(() => (
  activeLayer.value === 'primary' ? 'secondary' : 'primary'
))

const activeItem = computed(() => {
  const index = getLayerIndex(activeLayer.value)
  return typeof index === 'number' ? props.items[clampIndex(index)] || null : null
})

const videoLayers = computed(() => {
  return (['primary', 'secondary'] as const)
    .map((name) => {
      const index = getLayerIndex(name)
      const item = typeof index === 'number' ? props.items[clampIndex(index)] || null : null

      if (!item) {
        return null
      }

      const itemKey = getItemKey(item)

      return {
        name,
        item,
        itemKey,
        isActive: activeLayer.value === name,
        videoKey: `${name}-${itemKey}`
      }
    })
    .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer))
})

const selectItem = (index: number) => {
  const nextIndex = clampIndex(index)
  selectedIndex.value = nextIndex

  const nextItem = props.items[nextIndex]

  if (nextItem) {
    emit('change', { item: nextItem, index: selectedIndex.value })
  }

  if (!nextItem || nextIndex === getLayerIndex(activeLayer.value)) {
    return
  }

  const targetLayer = inactiveLayer.value
  const nextItemKey = getItemKey(nextItem)

  setLayerIndex(targetLayer, nextIndex)
  loadingLayer.value = targetLayer
  loadingItemKey.value = nextItemKey
}

const handleLayerReady = (
  layer: 'primary' | 'secondary',
  itemKey: string,
  event: Event
) => {
  const video = event.currentTarget as HTMLVideoElement | null

  if (video) {
    video.play().catch(() => {
      // Autoplay is muted, but browsers can still reject while a source is changing.
    })
  }

  if (loadingLayer.value !== layer || loadingItemKey.value !== itemKey) {
    return
  }

  activeLayer.value = layer
  loadingLayer.value = null
  loadingItemKey.value = ''
}

watch(
  () => props.initialIndex,
  (value) => {
    const nextIndex = clampIndex(value)
    selectedIndex.value = nextIndex
    setLayerIndex(activeLayer.value, nextIndex)
    setLayerIndex(inactiveLayer.value, null)
    loadingLayer.value = null
    loadingItemKey.value = ''
  },
  { immediate: true }
)

watch(
  () => props.items.length,
  () => {
    const nextIndex = clampIndex(selectedIndex.value)
    selectedIndex.value = nextIndex
    setLayerIndex(activeLayer.value, nextIndex)
    setLayerIndex(inactiveLayer.value, null)
    loadingLayer.value = null
    loadingItemKey.value = ''
  }
)

const updateViewportMode = () => {
  if (mobileQuery?.matches) {
    viewportMode.value = 'mobile'
    return
  }

  if (compactQuery?.matches) {
    viewportMode.value = 'compact'
    return
  }

  viewportMode.value = 'desktop'
}

onMounted(() => {
  if (typeof window === 'undefined') {
    return
  }

  compactQuery = window.matchMedia('(max-width: 1200px)')
  mobileQuery = window.matchMedia('(max-width: 640px)')
  updateViewportMode()
  compactQuery.addEventListener('change', updateViewportMode)
  mobileQuery.addEventListener('change', updateViewportMode)
})

onBeforeUnmount(() => {
  compactQuery?.removeEventListener('change', updateViewportMode)
  mobileQuery?.removeEventListener('change', updateViewportMode)
  compactQuery = null
  mobileQuery = null
})

return (_ctx: any,_cache: any) => {
  return (activeItem.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["pixso-tabbed-video", { 'pixso-tabbed-video--en': isEnglish.value }]),
        "data-text-reveal": "off"
      }, [
        _createElementVNode("figure", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(videoLayers.value, (layer) => {
            return (_openBlock(), _createElementBlock("video", {
              key: layer.videoKey,
              class: _normalizeClass(["pixso-tabbed-video__media", { 'pixso-tabbed-video__media--active': layer.isActive }]),
              poster: _ctx.poster || undefined,
              autoplay: "",
              muted: "",
              loop: "",
              playsinline: "",
              preload: "auto",
              "aria-label": layer.item.ariaLabel || layer.item.buttonLabel,
              onLoadeddata: ($event: any) => (handleLayerReady(layer.name, layer.itemKey, $event)),
              onCanplay: ($event: any) => (handleLayerReady(layer.name, layer.itemKey, $event))
            }, [
              _createElementVNode("source", {
                src: layer.item.src,
                type: "video/mp4"
              }, null, 8, _hoisted_3)
            ], 42, _hoisted_2))
          }), 128)),
          (_ctx.caption)
            ? (_openBlock(), _createElementBlock("figcaption", _hoisted_4, _toDisplayString(_ctx.caption), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", {
          class: "pixso-tabbed-video__tabs",
          role: "tablist",
          "aria-label": _ctx.tablistLabel
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(itemRows.value, (row, rowIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `tabbed-video-row-${rowIndex}`,
              class: "pixso-tabbed-video__row"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (item) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: item.key,
                  class: _normalizeClass(["pixso-tabbed-video__chip", { 'pixso-tabbed-video__chip--active': selectedIndex.value === item.itemIndex }]),
                  type: "button",
                  role: "tab",
                  "aria-selected": selectedIndex.value === item.itemIndex,
                  onClick: ($event: any) => (selectItem(item.itemIndex))
                }, _toDisplayString(item.buttonLabel), 11, _hoisted_6))
              }), 128))
            ]))
          }), 128))
        ], 8, _hoisted_5),
        (activeItem.value.subtitle)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(activeItem.value.subtitle), 1))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})